import { useState, useEffect, useCallback, useRef } from 'react';
import { FormProvider, useForm as useReactHookForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router';
import {
  ProtocolsEditor,
  type ProtocolsEditorHandle,
} from './ProtocolsEditor';
import {
  Typography,
  Table,
  Button,
  Modal,
  Drawer,
  Form,
  Input,
  InputNumber,
  Space,
  message,
  Switch,
  Tooltip,
  Select,
  AutoComplete,
  Tag,
  Alert,
  Segmented,
  Row,
  Col,
  Slider,
  Statistic,
  Spin,
  Empty,
  Descriptions,
} from 'antd';
import { CheckCircleTwoTone, WarningTwoTone, MinusCircleTwoTone } from '@ant-design/icons';
import { PlusOutlined } from '@ant-design/icons';
import { endpointHostValidator } from '../../utils/validators';
import { authFetch } from '../../utils/api';
import { DangerousActionModal, FormField, FormSection } from '../../shared/form';
import {
  canMutateSharedInfra,
  SHARED_INFRA_READONLY_MESSAGE,
} from '../../utils/permissions';
import { getBrandLabel } from '../../utils/tokenManager';
import { useBrandOptions } from '../../hooks/useBrandOptions';
import { BrandScopeNotice } from '../../components/BrandScopeNotice';
import {
  buildNodePayload,
  isNodeBrandScopeExplicit,
  MAX_NODE_TAG_LENGTH,
  MAX_NODE_TAGS,
  nodeToFormValues,
  parseTextList,
  type NodeCategory,
  type NodeFormValues,
  type NodeRecord,
  getMemberRevocationUiState,
  isMemberRevocationActionDisabled,
  memberRevocationActionRequest,
  memberRevocationLastCheckedLabel,
  memberRevocationHealthLabel,
  shouldPollMemberRevocationNodes,
  supportsMemberRevocation,
} from './nodeForm';
import {
  DEFAULT_FALLBACK_PRIORITY,
  DnsTxtDohEndpointSchema,
  FALLBACK_PRIORITY_OPTIONS,
  FINGERPRINT_PROTOCOL_KEYS,
  FINGERPRINT_PROTOCOL_LABELS,
  FINGERPRINT_VALUE_OPTIONS,
  NodeDeleteRemarkSchema,
  type NodeDeleteRemarkValues,
} from '../../schemas/node';

const { Title } = Typography;
const { TextArea } = Input;

const NODE_CATEGORY_OPTIONS: Array<{
  value: NodeCategory;
  label: string;
  color: string;
}> = [
  { value: 'primary', label: '主用節點', color: 'green' },
  { value: 'backup', label: '備援節點', color: 'gold' },
  { value: 'overseas', label: '海外節點', color: 'blue' },
  { value: 'internal', label: '內部/測試', color: 'default' },
];

type NodeCategoryFilter = NodeCategory | 'all';

/**
 * 2026-07-02 — 節點營運標記常用建議（Select mode="tags" 下拉提示；
 * operator 仍可自由輸入任意字）。
 */
const NODE_TAG_SUGGESTIONS = ['限速', '測試', '備援'];

/**
 * 2026-07-02 — 後端 node_brand_scope guard 的結構化 errorCode
 * （跨端 contract，對齊 vpn-manage-server utils/node_brand_scope.ts
 * NODE_BRAND_SCOPE_ERROR_CODE）。
 */
const NODE_BRAND_SCOPE_ERROR_CODE = 'NODE_BRAND_SCOPE_REQUIRED';

/** 品牌可見性未表態時的 inline 人話提示（新增模式前端擋 + 後端 400 掛回共用）。 */
const BRAND_SCOPE_INLINE_MESSAGE =
  '請選擇此節點對哪些品牌可見，或開啟「對所有品牌共享」';

function getNodeCategoryOption(value?: NodeCategory) {
  return (
    NODE_CATEGORY_OPTIONS.find(option => option.value === value) ??
    NODE_CATEGORY_OPTIONS[0]
  );
}

function validateRequiredTextList(
  value: unknown,
  messageText: string
): Promise<void> {
  if (parseTextList(value).length > 0) return Promise.resolve();
  return Promise.reject(new Error(messageText));
}

function validateHttpsUrlList(
  value: unknown,
  required = false
): Promise<void> {
  const entries = parseTextList(value);
  if (required && entries.length === 0) {
    return Promise.reject(
      new Error('啟用 DNS TXT Bootstrap 必須設定 DoH Endpoints')
    );
  }
  for (const entry of entries) {
    if (!DnsTxtDohEndpointSchema.safeParse(entry).success) {
      return Promise.reject(
        new Error('DNS TXT DoH Endpoints 只允許 HTTPS URL，且不可包含帳號密碼')
      );
    }
  }
  return Promise.resolve();
}

function isAntdValidationError(error: unknown): error is {
  errorFields: unknown[];
} {
  return (
    typeof error === 'object' &&
    error !== null &&
    'errorFields' in error &&
    Array.isArray((error as { errorFields?: unknown }).errorFields)
  );
}

/**
 * 節點管理頁面 — 完整 CRUD
 *
 * 編輯/新增節點面板採 Drawer 設計（>=1280px viewport）：
 * - 5 區 FormSection 拆分（基本資訊 / 可見性與計費 / 伺服器部署 / SK6 配置 / 進階防審查）
 * - 雙欄 grid 利用寬螢幕；mobile (<768) 自動降級單欄
 * - Sticky footer 把儲存/取消按鈕固定在底部，長表單 scroll 不會找不到按鈕
 * - viewport <1280px 自動 fallback 為 Modal width=720（vs 之前預設 600）
 */
function useDrawerLayout(): boolean {
  const [isWide, setIsWide] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 1024;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setIsWide(window.innerWidth >= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isWide;
}

export const NodeList: React.FC = () => {
  const sharedInfraReadOnly = !canMutateSharedInfra();
  const useDrawer = useDrawerLayout();
  const brandOptions = useBrandOptions();
  const [data, setData] = useState<NodeRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [keyword, setKeyword] = useState('');
  const [nodeCategory, setNodeCategory] =
    useState<NodeCategoryFilter>('all');
  // 2026-07-02 — 營運標記過濾（'' = 不過濾；帶值時打 GET /api/admin/nodes?tag=）
  const [tagFilter, setTagFilter] = useState('');
  const [memberRevocationActioningNodeIds, setMemberRevocationActioningNodeIds] =
    useState<Set<number>>(() => new Set());

  // 新增/編輯 Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalForm] = Form.useForm();
  const [modalLoading, setModalLoading] = useState(false);
  const [editingNode, setEditingNode] = useState<NodeRecord | null>(null);
  // 2026-07-02 — server 端儲存失敗常駐顯示（3 秒 toast 讀不完的根治）：
  // 任何 result.code!==0 都把 msg 存進來，於面板 footer 上方以 Alert 常駐，
  // 重送 / 關閉 / 成功時清除。
  const [serverError, setServerError] = useState<string | null>(null);
  const protocolsEditorRef = useRef<ProtocolsEditorHandle>(null);
  const [initialProtocols, setInitialProtocols] = useState<
    NodeFormValues['protocols']
  >([]);

  // 刪除確認（type-to-confirm）
  const [deleteTarget, setDeleteTarget] = useState<NodeRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const deleteRemarkForm = useReactHookForm<NodeDeleteRemarkValues>({
    resolver: zodResolver(NodeDeleteRemarkSchema),
    defaultValues: { remark: '' },
    mode: 'onChange',
  });

  // 節點監控 Drawer（即時連線/流量；5s auto-refresh）
  const [monitorTarget, setMonitorTarget] = useState<NodeRecord | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        sortBy: 'id',
        sortOrder: 'DESC',
      });
      if (keyword) params.set('keyword', keyword);
      if (nodeCategory !== 'all') params.set('nodeCategory', nodeCategory);
      if (tagFilter) params.set('tag', tagFilter);

      const res = await authFetch(`/api/admin/nodes?${params.toString()}`);
      const result = await res.json();
      if (result.code === 0 && result.data) {
        setData(Array.isArray(result.data.list) ? result.data.list : []);
        setTotal(result.data.total ?? 0);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, keyword, nodeCategory, tagFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll only while the currently rendered result contains a verification in
  // progress.  The derived boolean is deliberately stable so list refreshes
  // do not create overlapping timers or keep polling after a terminal result.
  const hasVerifyingNode = shouldPollMemberRevocationNodes(data);
  useEffect(() => {
    if (!hasVerifyingNode) return;
    const timer = window.setInterval(() => {
      void fetchData();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [fetchData, hasVerifyingNode]);

  const openAddModal = () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setEditingNode(null);
    setServerError(null);
    modalForm.resetFields();
    modalForm.setFieldsValue({
      nodeType: 'sk6',
      nodeCategory: 'primary',
      tags: [],
      port: 10137,
      minTier: 0,
      isEnabled: true,
      isDecoy: false,
      allowedBrands: [],
      shareAllBrands: false,
      fallbackPriority: [...DEFAULT_FALLBACK_PRIORITY],
      cdnRelayEnabled: false,
      cdnPort: 443,
      sk6WsPort: 80,
      sk6WsPath: '/',
      anytlsEnabled: false,
      anytlsPort: 443,
      dnsTxtDiscoveryEnabled: false,
      dnsTxtMaxAgeSec: 900,
      fingerprintMap: {},
      // Sprint 19 Wave 6 (W6-A) — DAITA defaults (disabled / empty).
      daitaEnabled: false,
      daitaMachineSpec: '',
      daitaReplyMachineSpec: '',
      daitaMaxPaddingFrac: 0.5,
      daitaMaxBlockingFrac: 0.1,
    });
    setInitialProtocols([]);
    protocolsEditorRef.current?.reset([]);
    setModalOpen(true);
  };

  const openEditModal = (record: NodeRecord) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setEditingNode(record);
    setServerError(null);
    const formValues = nodeToFormValues(record);
    modalForm.setFieldsValue(formValues);
    const nextProtocols = formValues.protocols ?? [];
    setInitialProtocols(nextProtocols);
    protocolsEditorRef.current?.reset(nextProtocols);
    setModalOpen(true);
  };

  const handleModalOk = async () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    try {
      // 重送前清掉上一輪 server 錯誤（避免舊 Alert 誤導這一輪結果）
      setServerError(null);
      const values = (await modalForm.validateFields()) as NodeFormValues;
      // SK6 節點收割獨立 RHF sub-form 的 protocols（含 zod 驗證）
      if ((values.nodeType || 'sk6') === 'sk6') {
        try {
          values.protocols = await protocolsEditorRef.current?.validateAndGet();
        } catch {
          message.error('動態協議欄位有誤，請檢查');
          return;
        }
      } else {
        values.protocols = [];
      }
      setModalLoading(true);
      const payload = buildNodePayload(values, editingNode?.config);

      const url = editingNode
        ? `/api/admin/nodes/${editingNode.id}`
        : '/api/admin/nodes';
      const method = editingNode ? 'PUT' : 'POST';

      const res = await authFetch(url, {
        method,
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.code === 0) {
        message.success(editingNode ? '節點已更新' : '節點新增成功');
        setModalOpen(false);
        modalForm.resetFields();
        setEditingNode(null);
        setServerError(null);
        fetchData();
      } else {
        // 2026-07-02 — server 端失敗根治：除 toast 外，一律把 msg 存進
        // serverError 於面板 footer 上方常駐顯示（不會 3 秒消失）。
        // 結構化 errorCode（node_brand_scope guard）再額外把錯誤掛回
        // 「可見品牌」欄位 inline + 自動捲過去，operator 一眼知道改哪裡。
        const msg = result.msg || (editingNode ? '更新失敗' : '新增失敗');
        if (result.data?.errorCode === NODE_BRAND_SCOPE_ERROR_CODE) {
          modalForm.setFields([
            { name: 'allowedBrands', errors: [BRAND_SCOPE_INLINE_MESSAGE] },
          ]);
          modalForm.scrollToField('allowedBrands');
          setServerError(
            `品牌可見性未設定：${BRAND_SCOPE_INLINE_MESSAGE}，再按一次儲存。`
          );
          message.warning('品牌可見性未設定，已用紅色標示欄位');
        } else {
          setServerError(msg);
          message.error(msg);
        }
      }
    } catch (error) {
      if (isAntdValidationError(error)) {
        // df13fe5 修了「伺服器部署三欄」的 silent drop,但這條 catch 對 antd
        // validateFields() 失敗原本「完全靜默」(只有欄位 inline 紅字、無 toast)。
        // 節點編輯是 5 分區長表單,失敗欄位常落在摺疊區/需捲動處,operator 看不到
        // 紅字就以為「按了沒反應 / 改了沒更新 / 這節點不能編輯」——正是同一類 silent
        // fail。補一個明確 toast + 自動捲到第一個錯誤欄位,消滅這條無提示路徑。
        const firstError = error.errorFields[0] as
          | { name?: unknown }
          | undefined;
        if (firstError?.name !== undefined) {
          modalForm.scrollToField(
            firstError.name as string | number | (string | number)[]
          );
        }
        message.warning('表單有欄位未通過驗證,已用紅色標示,請修正後再儲存');
      } else {
        message.error(error instanceof Error ? error.message : '表單驗證失敗');
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleModalCancel = () => {
    setModalOpen(false);
    modalForm.resetFields();
    setEditingNode(null);
    setServerError(null);
  };

  const handleToggle = async (id: number, isEnabled: boolean) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    try {
      const res = await authFetch(`/api/admin/nodes/${id}/toggle`, {
        method: 'PATCH',
        body: JSON.stringify({ isEnabled }),
      });
      const result = await res.json();
      if (result.code === 0) {
        message.success(isEnabled ? '節點已啟用' : '節點已停用');
        fetchData();
      } else {
        message.error(result.msg || '操作失敗');
      }
    } catch {
      message.error('操作失敗');
    }
  };

  const handleMemberRevocationAction = async (
    id: number,
    action: 'verify' | 'disable'
  ) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    const request = memberRevocationActionRequest(id, action);
    setMemberRevocationActioningNodeIds(current => new Set(current).add(id));
    try {
      const res = await authFetch(request.url, request.init);
      const result = await res.json();
      if (result.code === 0) {
        message.success(
          action === 'verify' ? '節點撤銷驗證已啟動' : '已停用會員撤銷'
        );
        fetchData();
      } else {
        message.error('操作失敗');
      }
    } catch {
      message.error('操作失敗');
    } finally {
      setMemberRevocationActioningNodeIds(current => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  };

  const openDeleteConfirmation = (node: NodeRecord) => {
    deleteRemarkForm.reset({ remark: '' });
    setDeleteTarget(node);
  };

  const closeDeleteConfirmation = () => {
    deleteRemarkForm.reset({ remark: '' });
    setDeleteTarget(null);
  };

  const handleDelete = async ({ remark }: NodeDeleteRemarkValues) => {
    if (!deleteTarget) return;
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    try {
      const res = await authFetch(`/api/admin/nodes/${deleteTarget.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ remark }),
      });
      const result = await res.json();
      if (result.code === 0) {
        message.success('節點已刪除');
        closeDeleteConfirmation();
        fetchData();
      } else {
        message.error(result.msg || '刪除失敗');
      }
    } catch {
      message.error('刪除失敗');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page-enter">
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, gap: 12 }}>
        <Title level={4} style={{ margin: 0 }} data-testid="nodes-title">節點管理</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openAddModal}
          disabled={sharedInfraReadOnly}
          data-testid="nodes-add-btn"
        >
          添加節點
        </Button>
      </div>

      <BrandScopeNotice kind="shared-infra" style={{ marginBottom: 12 }} />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="所有節點統一在此頁管理"
        description={
          <>
            「內部名稱」給後台辨識；「用戶端顯示地區/別名」才是 client
            看到的地區名稱。節點對各會員等級的可見範圍在
            <Link to="/tiers" style={{ margin: '0 4px' }}>等級管理</Link>
            頁設定（每個等級可選擇「全部節點」或「指定節點」白名單）。
          </>
        }
      />

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Segmented
          value={nodeCategory}
          options={[
            { value: 'all', label: '全部' },
            { value: 'primary', label: '主用節點' },
            { value: 'backup', label: '備援節點' },
            { value: 'overseas', label: '海外節點' },
            { value: 'internal', label: '內部/測試' },
          ]}
          onChange={(value) => {
            setNodeCategory(value as NodeCategoryFilter);
            setPage(1);
          }}
          data-testid="nodes-category-filter"
        />
        <Select
          allowClear
          placeholder="標記過濾"
          value={tagFilter || undefined}
          // ponytail: 選項聚合自當頁資料的 distinct tags（~60 節點單頁足夠）；
          // 節點量大到跨頁時再補後端 distinct-tags 端點。
          options={Array.from(new Set(data.flatMap(node => node.tags ?? [])))
            .sort()
            .map(tag => ({ value: tag, label: tag }))}
          onChange={(value?: string) => {
            setTagFilter(value ?? '');
            setPage(1);
          }}
          style={{ width: 160, maxWidth: '100%' }}
          data-testid="nodes-tag-filter"
        />
        <Input.Search
          placeholder="搜尋節點"
          allowClear
          onSearch={(value) => {
            setKeyword(value);
            setPage(1);
          }}
          style={{ width: 250, maxWidth: '100%' }}
          data-testid="nodes-search"
        />
      </div>

      <Table
        dataSource={data}
        loading={loading}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        size="small"
        data-testid="nodes-table"
        locale={{
          emptyText: loading ? undefined : (
            <div style={{ padding: '40px 0' }}>
              <p>尚無節點，新增 VPN 節點以開始服務</p>
              <Button type="primary" onClick={openAddModal}>建立第一個節點</Button>
            </div>
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 筆`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      >
        <Table.Column
          dataIndex="name"
          title="節點名稱"
          width={100}
          sorter={true}
          ellipsis={{ showTitle: false }}
          render={(name: string) => (
            <Tooltip title={name} placement="topLeft">
              <span data-testid="nodes-internal-name">{name}</span>
            </Tooltip>
          )}
        />
        <Table.Column
          dataIndex="displayName"
          title="顯示名稱"
          width={140}
          ellipsis={{ showTitle: false }}
          render={(displayName: string | undefined) => {
            const value = displayName?.trim();
            if (!value) {
              return (
                <Tooltip title="未設定，client 會 fallback 顯示地區/節點名稱" placement="topLeft">
                  <Typography.Text type="secondary" italic>
                    （未設定）
                  </Typography.Text>
                </Tooltip>
              );
            }
            return (
              <Tooltip title={value} placement="topLeft">
                <span data-testid="nodes-display-name">{value}</span>
              </Tooltip>
            );
          }}
        />
        <Table.Column
          dataIndex="region"
          title="地區"
          width={64}
          ellipsis={{ showTitle: false }}
          render={(region: string) => (
            <Tooltip title={region} placement="topLeft">
              <span>{region}</span>
            </Tooltip>
          )}
        />
        <Table.Column
          dataIndex="nodeType"
          title="協議"
          width={72}
          render={(type: NodeRecord['nodeType']) =>
            type === 'vless' ? <Tag color="purple">VLESS</Tag> : <Tag color="blue">SK6</Tag>
          }
        />
        <Table.Column
          dataIndex="ip"
          title="IP"
          width={108}
          ellipsis={{ showTitle: false }}
          render={(ip: string) => (
            <Tooltip title={ip} placement="topLeft">
              <span>{ip}</span>
            </Tooltip>
          )}
        />
        <Table.Column dataIndex="port" title="端口" width={56} />
        <Table.Column
          dataIndex="nodeCategory"
          title="分類"
          width={64}
          render={(category: NodeCategory, record: NodeRecord) => {
            const option = getNodeCategoryOption(category);
            return (
              <Space size={4}>
                <Tag color={option.color} style={{ marginRight: 0 }}>{option.label}</Tag>
                {record.isDecoy === true ? (
                  <Tooltip title="蜜罐節點">
                    <Tag color="red" data-testid="nodes-is-decoy-tag" style={{ marginRight: 0 }}>
                      蜜罐
                    </Tag>
                  </Tooltip>
                ) : null}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="tags"
          title="標記"
          width={120}
          render={(tags: NodeRecord['tags']) => {
            if (!tags?.length) {
              return <Typography.Text type="secondary">—</Typography.Text>;
            }
            // 超過 2 顆收成「前 2 顆 +N」，Tooltip 展開全列（列寬不爆）。
            const visible = tags.slice(0, 2);
            const hidden = tags.slice(2);
            return (
              <Tooltip title={tags.join('、')} placement="topLeft">
                <span data-testid="nodes-tags-cell">
                  {visible.map((tag) => (
                    <Tag key={tag} color="geekblue" style={{ marginRight: 4 }}>
                      {tag}
                    </Tag>
                  ))}
                  {hidden.length > 0 ? (
                    <Tag style={{ marginRight: 0 }}>+{hidden.length}</Tag>
                  ) : null}
                </span>
              </Tooltip>
            );
          }}
        />
        <Table.Column
          dataIndex="allowedBrands"
          title="品牌"
          width={72}
          ellipsis={{ showTitle: false }}
          render={(brands: NodeRecord['allowedBrands']) => {
            const labels = brands?.length
              ? brands.map((brand) => getBrandLabel(brand)).join('、')
              : '全部';
            return (
              <Tooltip title={labels} placement="topLeft">
                <span>
                  {brands?.length
                    ? brands.map((brand) => (
                        <Tag key={brand} style={{ marginRight: 4 }}>
                          {getBrandLabel(brand)}
                        </Tag>
                      ))
                    : <Tag>全部</Tag>}
                </span>
              </Tooltip>
            );
          }}
        />
        <Table.Column dataIndex="minTier" title="等級" width={56} />
        <Table.Column
          dataIndex="isEnabled"
          title="狀態"
          width={64}
          render={(_: unknown, record: NodeRecord) => {
            const switchEl = (
              <Switch
                checked={record.isEnabled !== false}
                onChange={(checked) => handleToggle(record.id, checked)}
                disabled={sharedInfraReadOnly}
                checkedChildren="啟"
                unCheckedChildren="停"
                size="small"
              />
            );
            return record.isEnabled === false && record.disabledReason ? (
              <Tooltip title={record.disabledReason}>{switchEl}</Tooltip>
            ) : (
              switchEl
            );
          }}
        />
        <Table.Column
          dataIndex="memberRevocationStatus"
          title="會員撤銷"
          width={232}
          render={(_: unknown, record: NodeRecord) => {
            if (!supportsMemberRevocation(record)) {
              return (
                <Tooltip title="VLESS 節點不執行 revoke agent">
                  <Typography.Text
                    type="secondary"
                    data-testid="member-revocation-not-applicable"
                  >
                    —
                  </Typography.Text>
                </Tooltip>
              );
            }
            const uiState = getMemberRevocationUiState(
              record.memberRevocationStatus,
              record.memberRevocationVerification,
              record.memberRevocationHealth
            );
            const lastCheckedLabel = memberRevocationLastCheckedLabel(
              record.memberRevocationVerification
            );
            const isActioning = memberRevocationActioningNodeIds.has(record.id);
            const tagColor =
              uiState.label === 'Ready'
                ? 'success'
                : uiState.label === 'Verifying'
                  ? 'processing'
                  : uiState.label === 'Needs check'
                    ? 'warning'
                    : 'default';
            return (
              <Space direction="vertical" size={2}>
                <Space size={4} wrap>
                  <Tag color={tagColor} data-testid="member-revocation-status">
                    {uiState.label}
                  </Tag>
                  <Tooltip
                    title={
                      uiState.actionDisabled
                        ? '驗證進行中，請等待節點回報結果'
                        : uiState.action === 'verify'
                          ? '驗證 node service 後才會啟用會員撤銷'
                          : '立即停用會員撤銷並取消進行中的驗證'
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        onClick={() =>
                          handleMemberRevocationAction(record.id, uiState.action)
                        }
                        disabled={
                          isMemberRevocationActionDisabled(
                            sharedInfraReadOnly,
                            isActioning
                          )
                        }
                        loading={isActioning}
                        data-testid="member-revocation-action"
                      >
                        {uiState.actionLabel}
                      </Button>
                    </span>
                  </Tooltip>
                </Space>
                {uiState.failureLabel ? (
                  <span
                    data-testid="member-revocation-failure"
                    style={{ color: '#d46b08', fontSize: 12 }}
                  >
                    {uiState.failureLabel}
                  </span>
                ) : null}
                {memberRevocationHealthLabel(record.memberRevocationHealth) ? (
                  <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                    {memberRevocationHealthLabel(record.memberRevocationHealth)}
                  </span>
                ) : null}
                {lastCheckedLabel ? (
                  <span
                    data-testid="member-revocation-last-checked"
                    style={{ color: '#8c8c8c', fontSize: 12 }}
                  >
                    {lastCheckedLabel}
                  </span>
                ) : null}
              </Space>
            );
          }}
        />
        <Table.Column dataIndex="id" title="ID" width={56} sorter={true} />
        <Table.Column
          title="操作"
          width={172}
          fixed="right"
          render={(_: unknown, record: NodeRecord) => (
            <Space size={4}>
              <Tooltip
                title={
                  record.monitoringEnabled
                    ? '即時連線/流量監控'
                    : '此節點未啟用監控（在編輯面板開啟「啟用監控」並於節點端開 Clash API）'
                }
              >
                <Button
                  size="small"
                  onClick={() => setMonitorTarget(record)}
                  disabled={!record.monitoringEnabled}
                  data-testid="nodes-monitor-btn"
                  aria-label="節點監控"
                >
                  監控
                </Button>
              </Tooltip>
              <Button
                size="small"
                onClick={() => openEditModal(record)}
                disabled={sharedInfraReadOnly}
                data-testid="nodes-edit-btn"
                aria-label="編輯節點"
              >
                編輯
              </Button>
              <Button
                size="small"
                danger
                data-testid="nodes-delete-btn"
                aria-label="刪除節點"
                disabled={sharedInfraReadOnly}
                onClick={() => openDeleteConfirmation(record)}
              >
                刪除
              </Button>
            </Space>
          )}
        />
      </Table>

      {/* 新增/編輯節點面板（>=1024 用 Drawer 寬版，否則 fallback Modal） */}
      <NodeFormPanel
        useDrawer={useDrawer}
        open={modalOpen}
        editingNode={editingNode}
        modalLoading={modalLoading}
        serverError={serverError}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
      >
        <NodeFormBody
          form={modalForm}
          isCreate={!editingNode}
          brandOptions={brandOptions}
          protocolsEditorRef={protocolsEditorRef}
          initialProtocols={initialProtocols}
        />
      </NodeFormPanel>

      {/* 節點監控 — 即時連線/流量（5s auto-refresh） */}
      <NodeMonitorDrawer
        node={monitorTarget}
        onClose={() => setMonitorTarget(null)}
      />

      {/* 刪除節點 — 危險動作 type-to-confirm */}
      <DangerousActionModal
        open={!!deleteTarget}
        title={`刪除節點：${deleteTarget?.name ?? ''}`}
        warning={
          <>
            此動作將軟刪除節點，所有連線到此節點的客戶端會在下次 urltest
            自動切換到其他節點。請確認已下發替代節點配置，避免服務中斷。
          </>
        }
        confirmKeyword={deleteTarget?.name ?? ''}
        confirmHint={
          <>
            為防止誤刪，請輸入節點名稱 <code>{deleteTarget?.name}</code> 確認：
          </>
        }
        confirming={deleting}
        extra={
          <FormProvider {...deleteRemarkForm}>
            <FormField<NodeDeleteRemarkValues>
              name="remark"
              label="刪除備註"
              required
              control={
                <TextArea
                  rows={3}
                  maxLength={500}
                  showCount
                  placeholder="請說明刪除此節點的原因；此內容會寫入操作審計日誌"
                  aria-label="刪除備註"
                />
              }
            />
          </FormProvider>
        }
        isExtraValid={deleteRemarkForm.formState.isValid}
        onConfirm={() => void deleteRemarkForm.handleSubmit(handleDelete)()}
        onCancel={closeDeleteConfirmation}
        confirmLabel="確認刪除節點"
      />
    </div>
  );
};

// =====================================================================
// 節點監控 Drawer — 即時連線/流量（sing-box Clash API 代理）
// =====================================================================

/** bytes → 人類可讀（B/KB/MB/GB/TB），保留 1 位小數。 */
function formatBytes(bytes: number): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const value = n / Math.pow(1024, i);
  return `${i === 0 ? value : value.toFixed(1)} ${units[i]}`;
}

interface MonitorStatusData {
  status: 'online' | 'offline' | 'disabled' | 'error';
  traffic: { upload: number; download: number };
  connectionCount: number;
  lastChecked: string;
}

interface MonitorConnectionRow {
  id: string;
  sourceIP: string;
  sourcePort: number;
  destination: string;
  network: string;
  upload: number;
  download: number;
  startTime: string;
  rule: string;
}

interface NodeMonitorDrawerProps {
  node: NodeRecord | null;
  onClose: () => void;
}

const MONITOR_REFRESH_MS = 5000;

const NodeMonitorDrawer: React.FC<NodeMonitorDrawerProps> = ({
  node,
  onClose,
}) => {
  const [status, setStatus] = useState<MonitorStatusData | null>(null);
  const [connections, setConnections] = useState<MonitorConnectionRow[]>([]);
  const [loading, setLoading] = useState(false);
  // API 層錯誤（authFetch throw / 非 2xx / code≠0）— 跟「節點離線」分開呈現，
  // 讓 operator 知道是 BO API 掛了還是節點真的連不到。
  const [apiError, setApiError] = useState<string | null>(null);
  const open = node !== null;
  const nodeId = node?.id ?? null;
  // 用 ref 持有最新 nodeId，避免 interval closure 抓到舊值
  const nodeIdRef = useRef<number | null>(null);
  nodeIdRef.current = nodeId;

  const fetchMonitor = useCallback(async () => {
    const id = nodeIdRef.current;
    if (id === null) return;
    try {
      const [statusRes, connRes] = await Promise.all([
        authFetch(`/api/admin/nodes/${id}/monitor`),
        authFetch(`/api/admin/nodes/${id}/connections`),
      ]);
      // 切換節點 race 防護：回應回來時 target 可能已換，丟棄過期結果
      if (nodeIdRef.current !== id) return;
      // authFetch 非 2xx 不會 throw → 自己檢查 HTTP 狀態（區分 API 掛 vs 節點離線）
      if (!statusRes.ok || !connRes.ok) {
        const code = !statusRes.ok ? statusRes.status : connRes.status;
        setApiError(`監控 API 回應異常（HTTP ${code}）— 這是 BO API 問題，非節點離線`);
        return;
      }
      const statusJson = await statusRes.json();
      const connJson = await connRes.json();
      if (nodeIdRef.current !== id) return;
      if (statusJson.code !== 0 || connJson.code !== 0) {
        const msg =
          statusJson.msg || connJson.msg || '監控 API 回傳錯誤碼';
        setApiError(`監控 API 錯誤：${msg}`);
        return;
      }
      setApiError(null);
      if (statusJson.data) {
        setStatus(statusJson.data as MonitorStatusData);
      }
      if (connJson.data) {
        setConnections(
          Array.isArray(connJson.data.connections)
            ? (connJson.data.connections as MonitorConnectionRow[])
            : []
        );
      }
    } catch (e) {
      // 網路層失敗（fetch reject / 解析失敗）：保留前次資料，但標示 API 錯誤。
      if (nodeIdRef.current !== id) return;
      setApiError(
        `無法呼叫監控 API（${
          e instanceof Error ? e.message : '網路錯誤'
        }）— 這是 BO 端問題，非節點離線`
      );
    } finally {
      if (nodeIdRef.current === id) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || nodeId === null) {
      // 關閉時清空，下次打開不殘留舊節點資料
      setStatus(null);
      setConnections([]);
      setApiError(null);
      return;
    }
    setLoading(true);
    setStatus(null);
    setConnections([]);
    setApiError(null);
    fetchMonitor();
    const timer = setInterval(fetchMonitor, MONITOR_REFRESH_MS);
    return () => clearInterval(timer);
  }, [open, nodeId, fetchMonitor]);

  const statusTag = (() => {
    const s = status?.status;
    if (s === 'online') return <Tag color="success">線上</Tag>;
    if (s === 'disabled')
      return <Tag color="default">未啟用（節點端未開 Clash API）</Tag>;
    if (s === 'error') return <Tag color="warning">錯誤</Tag>;
    return <Tag color="error">離線</Tag>;
  })();

  return (
    <Drawer
      title={node ? `節點監控：${node.displayName || node.name}` : '節點監控'}
      open={open}
      onClose={onClose}
      width={600}
      destroyOnClose
      extra={statusTag}
    >
      {apiError && (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          message="監控 API 呼叫失敗"
          description={apiError}
          onClose={() => setApiError(null)}
        />
      )}
      {status === null && loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Spin tip="讀取中…" />
        </div>
      ) : (
        <>
          <Descriptions
            size="small"
            column={1}
            bordered
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="節點">
              {node?.name}（{node?.ip}:{node?.monitoringPort ?? 9090}）
            </Descriptions.Item>
            <Descriptions.Item label="狀態">{statusTag}</Descriptions.Item>
            <Descriptions.Item label="最後更新">
              {status?.lastChecked
                ? new Date(status.lastChecked).toLocaleTimeString()
                : '—'}
            </Descriptions.Item>
          </Descriptions>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Statistic
                title="連線數"
                value={status?.connectionCount ?? 0}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="即時上傳"
                value={formatBytes(status?.traffic.upload ?? 0)}
                valueStyle={{ fontSize: 18 }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="即時下載"
                value={formatBytes(status?.traffic.download ?? 0)}
                valueStyle={{ fontSize: 18 }}
              />
            </Col>
          </Row>

          {status?.status === 'disabled' ? (
            <Alert
              type="warning"
              showIcon
              message="節點端尚未啟用 Clash API"
              description="請在節點 sing-box config 設 external-controller 0.0.0.0:<port> + secret，並用防火牆只放行 BO IP（16.163.172.36）。詳見 docs/operations/2026-06-11-node-monitoring-enablement.md。"
            />
          ) : status?.status !== 'online' ? (
            <Alert
              type="error"
              showIcon
              message="節點離線或無法連線"
              description="BO 連不到節點監控端口。可能：節點端未開 external-controller、防火牆未放行 BO IP、secret 不符、或 MONITORING_PROTOCOL（http/https）設定不符。"
            />
          ) : (
            <>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                即時連線（{connections.length}）
              </Typography.Text>
              <Table
                size="small"
                rowKey="id"
                dataSource={connections}
                pagination={{ pageSize: 10, size: 'small' }}
                scroll={{ x: 520 }}
                locale={{
                  emptyText: <Empty description="目前沒有活躍連線" />,
                }}
              >
                <Table.Column
                  title="來源"
                  dataIndex="sourceIP"
                  width={120}
                  ellipsis
                />
                <Table.Column
                  title="目標"
                  dataIndex="destination"
                  ellipsis
                />
                <Table.Column
                  title="協議"
                  dataIndex="network"
                  width={64}
                />
                <Table.Column
                  title="上傳"
                  dataIndex="upload"
                  width={80}
                  render={(v: number) => formatBytes(v)}
                />
                <Table.Column
                  title="下載"
                  dataIndex="download"
                  width={80}
                  render={(v: number) => formatBytes(v)}
                />
              </Table>
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

// =====================================================================
// 節點編輯 Panel — Drawer (>=1024) / Modal fallback wrapper
// =====================================================================

interface NodeFormPanelProps {
  useDrawer: boolean;
  open: boolean;
  editingNode: NodeRecord | null;
  modalLoading: boolean;
  /**
   * 2026-07-02 — server 端儲存失敗常駐 Alert（渲染在儲存按鈕旁，operator
   * 按下按鈕的視線落點；不像 toast 3 秒就消失）。null = 無錯誤。
   */
  serverError?: string | null;
  onOk: () => void;
  onCancel: () => void;
  children: React.ReactNode;
}

const NodeFormPanel: React.FC<NodeFormPanelProps> = ({
  useDrawer,
  open,
  editingNode,
  modalLoading,
  serverError,
  onOk,
  onCancel,
  children,
}) => {
  const title = editingNode ? `編輯節點：${editingNode.name}` : '新增節點';
  const serverErrorAlert = serverError ? (
    <Alert
      type="error"
      showIcon
      message="伺服器拒絕了這次儲存"
      description={serverError}
      data-testid="nodes-form-server-error"
    />
  ) : null;
  const footerNode = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {serverErrorAlert}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 12,
        }}
      >
        <Button
          onClick={onCancel}
          disabled={modalLoading}
          data-testid="nodes-form-cancel"
        >
          取消
        </Button>
        <Button
          type="primary"
          onClick={onOk}
          loading={modalLoading}
          data-testid="nodes-form-submit"
        >
          {editingNode ? '儲存變更' : '建立節點'}
        </Button>
      </div>
    </div>
  );

  if (useDrawer) {
    return (
      <Drawer
        title={title}
        open={open}
        onClose={onCancel}
        width="min(1280px, 85vw)"
        destroyOnClose
        styles={{
          body: { paddingBottom: 24 },
          footer: { padding: '12px 24px' },
        }}
        footer={footerNode}
        maskClosable={!modalLoading}
      >
        {children}
      </Drawer>
    );
  }

  return (
    <Modal
      title={title}
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      confirmLoading={modalLoading}
      okText={editingNode ? '儲存變更' : '建立節點'}
      cancelText="取消"
      width={720}
      destroyOnClose
    >
      {children}
      {/* Modal fallback：Alert 放內容最下方（緊鄰 ok 按鈕視線落點） */}
      {serverErrorAlert ? (
        <div style={{ marginTop: 12 }}>{serverErrorAlert}</div>
      ) : null}
    </Modal>
  );
};

// =====================================================================
// 節點編輯表單主體（5 區 FormSection + 雙欄 grid）
// =====================================================================

interface NodeFormBodyProps {
  form: ReturnType<typeof Form.useForm>[0];
  /**
   * 2026-07-02 — 新增模式（!editingNode）。品牌可見性必選驗證只在新增
   * 模式生效（鏡像後端 guard 只攔 CREATE 的語意；編輯 29 個既有
   * NULL-brand 節點不受影響）。
   */
  isCreate: boolean;
  brandOptions: ReturnType<typeof useBrandOptions>;
  protocolsEditorRef: React.RefObject<ProtocolsEditorHandle | null>;
  initialProtocols: NodeFormValues['protocols'];
}

/** 通用 grid 設定：寬螢幕兩欄、tablet 兩欄、mobile 單欄 */
const COL_HALF = { xs: 24, sm: 24, md: 12 } as const;
const COL_FULL = { xs: 24 } as const;
const ROW_GUTTER: [number, number] = [24, 0];

const NodeFormBody: React.FC<NodeFormBodyProps> = ({
  form,
  isCreate,
  brandOptions,
  protocolsEditorRef,
  initialProtocols,
}) => {
  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        nodeType: 'sk6',
        nodeCategory: 'primary',
        tags: [],
        port: 10137,
        minTier: 0,
        isEnabled: true,
        isDecoy: false,
        allowedBrands: [],
        shareAllBrands: false,
        fallbackPriority: [...DEFAULT_FALLBACK_PRIORITY],
        cdnRelayEnabled: false,
        cdnPort: 443,
        sk6WsPort: 80,
        sk6WsPath: '/',
        anytlsEnabled: false,
        anytlsPort: 443,
        dnsTxtDiscoveryEnabled: false,
        dnsTxtMaxAgeSec: 900,
        fingerprintMap: {},
        // Sprint 19 Wave 6 (W6-A) — DAITA defaults (disabled / empty).
        daitaEnabled: false,
        daitaMachineSpec: '',
        daitaReplyMachineSpec: '',
        daitaMaxPaddingFrac: 0.5,
        daitaMaxBlockingFrac: 0.1,
      }}
    >
      {/* === 1. 基本資訊 === */}
      <FormSection
        title="基本資訊"
        description="節點識別、地區與分類；client 看到的是「顯示名稱」，operator 用「節點名稱」管理。"
      >
        <Row gutter={ROW_GUTTER}>
          <Col {...COL_HALF}>
            <Form.Item
              name="nodeType"
              label="協議類型"
              rules={[{ required: true, message: '請選擇協議類型' }]}
            >
              <Select>
                <Select.Option value="sk6">SK6（自有節點）</Select.Option>
                <Select.Option value="vless">VLESS（Cloudflare）</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="nodeCategory"
              label="節點分類"
              tooltip="分類只控制 BO 管理和營運視圖；品牌可見性仍由「可見品牌」與等級節點白名單控制。"
              rules={[{ required: true, message: '請選擇節點分類' }]}
            >
              <Select
                options={NODE_CATEGORY_OPTIONS.map(({ value, label }) => ({
                  value,
                  label,
                }))}
              />
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="tags"
              label="標記"
              tooltip="BO 內部營運標記，可自由輸入多個；不會下發到用戶端 App。想標「這節點是誰家的、有什麼限制」就用這裡，不要塞進顯示名稱。"
              rules={[
                {
                  validator: async (_, value?: string[]) => {
                    if (!value?.length) return;
                    if (value.length > MAX_NODE_TAGS) {
                      throw new Error(`標記最多 ${MAX_NODE_TAGS} 個`);
                    }
                    for (const tag of value) {
                      if (tag.trim().length > MAX_NODE_TAG_LENGTH) {
                        throw new Error(
                          `每個標記最長 ${MAX_NODE_TAG_LENGTH} 字`
                        );
                      }
                    }
                  },
                },
              ]}
            >
              <Select
                mode="tags"
                allowClear
                tokenSeparators={[',', '，']}
                maxTagTextLength={MAX_NODE_TAG_LENGTH}
                placeholder="輸入後按 Enter 新增（例：限速、測試、備援、廠商名）"
                options={NODE_TAG_SUGGESTIONS.map(tag => ({
                  value: tag,
                  label: tag,
                }))}
                data-testid="nodes-tags-input"
              />
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="name"
              label="節點名稱"
              tooltip="internal/operations 用（operator 看的，建議 ASCII identifier，例如 ak-hk-01、vip-ali-hy01）"
              rules={[
                { required: true, message: '請輸入節點名稱' },
                {
                  pattern: /^[A-Za-z0-9._\- ]+$/,
                  message: '只允許英數、點、底線、連字號、空白',
                },
              ]}
            >
              <Input placeholder="例如 ak-hk-01 或 vip-ali-hy01" />
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="displayName"
              label="顯示名稱"
              tooltip="用戶端 dashboard 會顯示這個值（中文友善名）。例如「香港 ak01」「海外加速」。空白時 client 會 fallback 顯示地區/節點名稱。"
              rules={[{ max: 128, message: '顯示名稱最多 128 字' }]}
            >
              <Input placeholder="例如 香港 ak01、海外加速" />
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="ip"
              label="IP / 域名"
              tooltip="節點伺服器 IP（operator 直接登入用；BO 拿來推送協議配置）。"
              rules={[
                { required: true, message: '請輸入 IP 或域名' },
                { validator: endpointHostValidator },
              ]}
            >
              <Input placeholder="例如 120.76.228.116 或 node.example.com" />
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="port"
              label="端口"
              rules={[
                { required: true, message: '請輸入端口' },
                { type: 'number', min: 1, max: 65535, message: '端口範圍為 1-65535' },
              ]}
            >
              <InputNumber min={1} max={65535} placeholder="請輸入端口" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="region"
              label="地區"
              tooltip="可從常用地區選擇，或直接輸入自訂值（自由格式，後端不限制 enum）。中國地區除深圳外也可選/自訂其他城市。client 顯示時優先用「顯示名稱」。"
              rules={[
                { required: true, message: '請選擇或輸入地區' },
                { max: 255, message: '地區最長 255 字' },
              ]}
            >
              {/* Bug 2 fix — AutoComplete 是 string-valued combobox：可從預設清單
                  選擇，也可自由輸入任意自訂值（後端 node DTO region 接受自由格式
                  string，無 enum 限制）。原本是 Select 只能挑固定選項，中國只有
                  「深圳」可選，無法新增其他城市/自訂。filterOption 對 label + value
                  雙向 case-insensitive 過濾。 */}
              <AutoComplete
                allowClear={false}
                placeholder="選擇常用地區或輸入自訂值（例：北京 / 上海 / sz / 自訂機房）"
                filterOption={(input, option) => {
                  const q = input.trim().toLowerCase();
                  if (!q) return true;
                  const label = String(option?.label ?? '').toLowerCase();
                  const value = String(option?.value ?? '').toLowerCase();
                  return label.includes(q) || value.includes(q);
                }}
                options={[
                  // 海外地區
                  { value: 'hk', label: '香港 (hk)' },
                  { value: 'au', label: '澳洲 (au)' },
                  { value: 'tw', label: '台灣 (tw)' },
                  { value: 'jp', label: '日本 (jp)' },
                  { value: 'us', label: '美國 (us)' },
                  { value: 'sg', label: '新加坡 (sg)' },
                  { value: 'kr', label: '韓國 (kr)' },
                  // 中國地區（深圳以外的常用城市；仍可自由輸入未列出的值）
                  { value: 'sz', label: '深圳 (sz)' },
                  { value: 'bj', label: '北京 (bj)' },
                  { value: 'sh', label: '上海 (sh)' },
                  { value: 'gz', label: '廣州 (gz)' },
                  { value: 'hz', label: '杭州 (hz)' },
                  { value: 'cd', label: '成都 (cd)' },
                  { value: 'nj', label: '南京 (nj)' },
                  { value: 'wh', label: '武漢 (wh)' },
                  // 邏輯分類
                  { value: 'overseas', label: '海外 (overseas)' },
                  { value: 'overseas_accel', label: '海外加速 (overseas_accel)' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="urltestUrl"
              label="Urltest URL"
              tooltip="客戶端弱網/切換時用 HTTPS probe 驗證連線，不填則使用客戶端預設。"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value?.trim()) return;
                    const url = new URL(value.trim());
                    if (url.protocol !== 'https:') {
                      throw new Error('Urltest URL 必須是 HTTPS');
                    }
                  },
                },
              ]}
            >
              <Input placeholder="https://connectivitycheck.swiftmesh.net/generate_204" />
            </Form.Item>
          </Col>
        </Row>
      </FormSection>

      {/* === 2. 可見性與計費 === */}
      <FormSection
        title="可見性與計費"
        description="決定哪些品牌、哪些等級的用戶看得到此節點，以及是否作為蜜罐節點誘捕逆向行為。"
      >
        <Row gutter={ROW_GUTTER}>
          <Col {...COL_HALF}>
            {/* 2026-07-02 — 「對所有品牌共享」互斥防呆：切 ON 時清空並 disable
                「可見品牌」，根除「兩者都設但後端實存限定清單」的語意落差
                （後端行為：allowedBrands 非空時 shareAllBrands 只是 guard
                bypass 訊號，最終存的是限定清單）。用 shouldUpdate render-prop
                訂閱 shareAllBrands（Form.useWatch 在 React 19 + antd 5.25
                不觸發 re-render，實測 extra/disabled 不更新）。 */}
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) =>
                prev.shareAllBrands !== cur.shareAllBrands
              }
            >
              {() => {
                const shareAllBrands =
                  form.getFieldValue('shareAllBrands') === true;
                return (
                  <Form.Item
                    name="allowedBrands"
                    label="可見品牌"
                    // 新增模式標 required 星號（驗證邏輯在下方 validator，
                    // 「開啟對所有品牌共享」也算已表態）
                    required={isCreate}
                    tooltip="選 K盾 表示只下發給 K盾。新增節點必須選至少一個品牌，或開啟旁邊「對所有品牌共享」——不再「不選=全部可見」（曾導致免費用戶連到限定節點，2026-06-11 起後端 fail-closed）。"
                    extra={
                      shareAllBrands
                        ? '已對所有品牌共享，毋須再選品牌'
                        : isCreate
                          ? '新增節點必須擇一：選定可見品牌，或開啟「對所有品牌共享」'
                          : undefined
                    }
                    rules={[
                      {
                        // 2026-07-02 — 新增模式前端就擋下空品牌 payload（鏡像
                        // 後端 assertNodeBrandScopeExplicit，根除盲 400）；
                        // 編輯模式放行（後端 guard 只攔 CREATE）。
                        validator: async () => {
                          if (!isCreate) return;
                          if (
                            isNodeBrandScopeExplicit({
                              allowedBrands:
                                form.getFieldValue('allowedBrands'),
                              shareAllBrands:
                                form.getFieldValue('shareAllBrands'),
                            })
                          ) {
                            return;
                          }
                          throw new Error(BRAND_SCOPE_INLINE_MESSAGE);
                        },
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      disabled={shareAllBrands}
                      placeholder={
                        shareAllBrands ? '對所有品牌共享中' : '請選擇可見品牌'
                      }
                      options={brandOptions.map((option) => ({
                        value: option.value,
                        label: option.label,
                      }))}
                      data-testid="nodes-allowed-brands-select"
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="shareAllBrands"
              label="對所有品牌共享"
              valuePropName="checked"
              tooltip="明確 opt-in 讓此節點對「所有品牌（含未來新增品牌）」可見，用於跨品牌共享節點（如海外加速）。新增節點若沒選可見品牌，必須開啟此項才能建立。一般限定品牌節點請改用左方「可見品牌」。"
            >
              <Switch
                checkedChildren="全品牌"
                unCheckedChildren="限定"
                data-testid="nodes-share-all-brands-switch"
                onChange={(checked) => {
                  // 互斥防呆：開啟共享時清空限定清單（避免「兩者都設」
                  // 實存限定清單的語意落差），並重跑可見品牌欄位驗證
                  // 讓 inline 錯誤即時清掉。
                  if (checked) {
                    form.setFieldsValue({ allowedBrands: [] });
                  }
                  form.validateFields(['allowedBrands']).catch(() => {
                    /* inline 錯誤由 Form.Item 呈現 */
                  });
                }}
              />
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="minTier"
              label="最低等級"
              tooltip="0 表示所有用戶；2 以上通常只給付費/內測等級。"
              rules={[{ type: 'number', min: 0, max: 999, message: '最低等級範圍為 0-999' }]}
            >
              <InputNumber min={0} max={999} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item name="isEnabled" label="啟用狀態" valuePropName="checked">
              <Switch checkedChildren="啟用" unCheckedChildren="停用" />
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="isDecoy"
              label="蜜罐節點 (AR-Q7)"
              valuePropName="checked"
              tooltip="開啟後客戶端 manifest 不會下發此節點，但節點伺服器仍會運行；只有反組譯 binary、直接 poke API 的逆向工程者會打到，並被 vpn_decoy_connection 表記錄。"
            >
              <Switch
                checkedChildren="蜜罐"
                unCheckedChildren="一般"
                data-testid="nodes-is-decoy-switch"
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="expectedExitIpsText"
              label="預期出口 IP（每行一個）"
              tooltip="每行一個。relayOnly / originHidden / production Hysteria2 必須有出口證據；VLESS Worker 若無穩定出口會標記為 dynamic-non-strict。"
            >
              <TextArea rows={3} placeholder={'18.162.167.29\n52.62.127.13'} />
            </Form.Item>
          </Col>
        </Row>
      </FormSection>

      {/* === 3. 伺服器部署 === */}
      <FormSection
        title="伺服器部署"
        description="Jenkins SCP 推送 sing-box binary 用的部署參數；非 AWS HK/AU 預設節點才需要填寫。"
      >
        <Row gutter={ROW_GUTTER}>
          <Col {...COL_HALF}>
            <Form.Item
              name="sshCredentialId"
              label="Jenkins SSH credential id"
              tooltip="Jenkins UI 裡 Manage Jenkins → Credentials 建立的 SSH credential id；空白表示用 legacy AWS HK/AU 預設(rpvpn-aws-pem)。Akile-HK01 之類自架節點要自己填(例如 akile-hk01-key)。"
              rules={[
                {
                  pattern: /^[A-Za-z0-9._-]+$/,
                  message: '只允許英數、點、底線、連字號（Jenkins credential id 慣例）',
                },
                { max: 64, message: '長度最多 64 字' },
              ]}
            >
              <Input
                placeholder="留空 = rpvpn-aws-pem（legacy AWS HK/AU 預設）"
                allowClear
              />
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="serviceName"
              label="systemd 服務名稱"
              tooltip="Jenkins SCP 完 binary 後 systemctl restart 的 unit 名稱；空白表示用預設 sing-box。Akile 用 sk6。"
              rules={[
                {
                  pattern: /^[A-Za-z0-9._-]+$/,
                  message: '只允許英數、點、底線、連字號',
                },
                { max: 64, message: '長度最多 64 字' },
              ]}
            >
              <Input
                placeholder="留空 = sing-box（預設）"
                allowClear
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="deployPath"
              label="Deploy 路徑"
              tooltip="Target server 上 sing-box binary 應該被 Jenkins SCP 進去的絕對路徑；空白表示用預設 /usr/local/bin/sing-box。Akile 等手動節點通常用 /home/rpvpn-deploy/ver2/box.elf。"
              rules={[
                {
                  pattern: /^\/[A-Za-z0-9._/-]+$/,
                  message:
                    '必須是絕對路徑（以 / 開頭，只允許英數、點、底線、連字號與斜線）',
                },
                { max: 256, message: '路徑最多 256 字' },
              ]}
            >
              <Input
                placeholder="留空 = /usr/local/bin/sing-box（預設）"
                allowClear
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="backupDomainsText"
              label="Fallback / 備用域名（每行一個）"
              tooltip="每行一個；VLESS 會同步寫入 config.backupDomains。"
            >
              <TextArea rows={3} placeholder="relay-01.example.com" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="節點監控（sing-box Clash API）"
              description="啟用後 BO 可即時看此節點的連線數/流量。需先在節點端開啟 sing-box external-controller（0.0.0.0:<port> + secret）並用防火牆只放行 BO IP，否則監控面板會顯示節點離線。詳見 docs/operations/2026-06-11-node-monitoring-enablement.md。"
            />
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="monitoringEnabled"
              label="啟用監控"
              tooltip="開啟後節點操作列出現「監控」按鈕；需節點端已開 Clash API external-controller。"
              valuePropName="checked"
            >
              <Switch checkedChildren="開" unCheckedChildren="關" />
            </Form.Item>
          </Col>
          <Col {...COL_HALF}>
            <Form.Item
              name="monitoringPort"
              label="監控端口"
              tooltip="sing-box external-controller 監聽端口（Clash API）。預設 9090。"
            >
              <InputNumber min={1} max={65535} style={{ width: '100%' }} placeholder="9090" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 0 }}
              message="監控 Secret 不在此表單設定（安全邊界）"
              description="Clash API secret 與節點認證 secret 同屬敏感欄位，後端刻意不開放從節點表單寫入（allowedKeys 安全契約）。請在節點端 sing-box config 直接設 external-controller secret，並由運維在 DB / 部署流程帶入 BO（見 docs/operations/2026-06-11-node-monitoring-enablement.md）。"
            />
          </Col>
        </Row>
      </FormSection>

      {/* === 4. 節點配置（依 nodeType 動態切換） === */}
      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.nodeType !== cur.nodeType}>
        {({ getFieldValue }) =>
          getFieldValue('nodeType') === 'vless' ? (
            <FormSection
              title="VLESS 配置"
              description="Cloudflare Workers 中繼節點；UUID 通常為 Worker static UUID。"
            >
              <Row gutter={ROW_GUTTER}>
                <Col xs={24}>
                  <Form.Item
                    name="vlessUuid"
                    label="VLESS UUID"
                    rules={[{ required: true, message: '請輸入 VLESS UUID' }]}
                  >
                    <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                  </Form.Item>
                </Col>
                <Col {...COL_HALF}>
                  <Form.Item
                    name="vlessWsHost"
                    label="WebSocket Host"
                    rules={[{ required: true, message: '請輸入 WebSocket Host' }]}
                  >
                    <Input placeholder="relay.example.com" />
                  </Form.Item>
                </Col>
                <Col {...COL_HALF}>
                  <Form.Item name="vlessWsPath" label="WebSocket Path">
                    <Input placeholder="/（預設）" />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="vlessSni" label="TLS SNI">
                    <Input placeholder="未填則使用 WebSocket Host" />
                  </Form.Item>
                </Col>
              </Row>
            </FormSection>
          ) : (
            <>
              <FormSection
                title="SK6 節點配置"
                description="多協議 fallback、CDN relay、SK6-WS Origin 設定，以及客戶端共享的 SK6 config JSON。"
              >
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="多協議節點配置"
                  description="以下欄位會寫入 SK6 config；relayOnly / originHidden 需要 CDN relay，DNS TXT discovery 只作 bootstrap，實際節點仍由後端簽發。"
                />
                <Row gutter={ROW_GUTTER}>
                  <Col {...COL_HALF}>
                    <Form.Item name="relayOnly" label="只走中繼" valuePropName="checked">
                      <Switch checkedChildren="啟用" unCheckedChildren="停用" />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item name="originHidden" label="隱藏 Origin" valuePropName="checked">
                      <Switch checkedChildren="啟用" unCheckedChildren="停用" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name="fallbackPriority"
                      label="Fallback Chain"
                      tooltip="順序固定為 SK6 → Reality → AnyTLS → Hysteria2；後端與客戶端都會鎖定此抗封鎖順序，不能手動把弱網/實驗協議排到前面。"
                    >
                      <Select
                        mode="multiple"
                        disabled
                        options={FALLBACK_PRIORITY_OPTIONS.map((option) => ({
                          value: option.value,
                          label: option.label,
                        }))}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                      CDN Relay / SK6-WS
                    </Typography.Text>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item name="cdnRelayEnabled" label="啟用 CDN Relay" valuePropName="checked">
                      <Switch checkedChildren="啟用" unCheckedChildren="停用" />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item
                      name="cdnDomain"
                      label="CDN Domain"
                      rules={[{ validator: endpointHostValidator }]}
                    >
                      <Input placeholder="edge.example.com" />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item
                      name="cdnPort"
                      label="CDN Port"
                      rules={[{ type: 'number', min: 1, max: 65535, message: '端口範圍為 1-65535' }]}
                    >
                      <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item
                      name="sk6WsPort"
                      label="Origin WS Port"
                      rules={[{ type: 'number', min: 1, max: 65535, message: '端口範圍為 1-65535' }]}
                    >
                      <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name="sk6WsPath" label="Origin WS Path">
                      <Input placeholder="/ws" />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Typography.Text strong style={{ display: 'block', marginTop: 8, marginBottom: 8 }}>
                      AnyTLS
                    </Typography.Text>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item name="anytlsEnabled" label="啟用 AnyTLS" valuePropName="checked">
                      <Switch checkedChildren="啟用" unCheckedChildren="停用" />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item
                      name="anytlsPort"
                      label="AnyTLS Port"
                      rules={[{ type: 'number', min: 1, max: 65535, message: '端口範圍為 1-65535' }]}
                    >
                      <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item
                      name="anytlsDomain"
                      label="AnyTLS Domain"
                      rules={[{ validator: endpointHostValidator }]}
                    >
                      <Input placeholder="endpoint.swiftmesh.net" />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item
                      name="anytlsSni"
                      label="AnyTLS SNI"
                      rules={[{ validator: endpointHostValidator }]}
                    >
                      <Input placeholder="未填則由 client 使用 domain" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name="anytlsPassword" label="AnyTLS 密碼">
                      <Input.Password placeholder="AnyTLS password" />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item
                      name="config"
                      label="SK6 Config (JSON)"
                      help="WS CDN 的歷史驗證紀錄不影響協議下發；仍須完整連線設定與有效授權。"
                      rules={[
                        {
                          validator: async (_, value) => {
                            if (!value?.trim()) return;
                            JSON.parse(value);
                          },
                          message: '請輸入合法 JSON',
                        },
                      ]}
                    >
                      <TextArea
                        rows={10}
                        placeholder='例如 {"sk6WsPath":"/"}，可留空'
                        style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 13 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label="動態協議 fallback"
                      tooltip="Trojan / Hysteria2 / VLESS-Reality，客戶端 urltest 自動納入容災"
                      style={{ marginBottom: 0 }}
                    >
                      <ProtocolsEditor
                        ref={protocolsEditorRef}
                        initialProtocols={initialProtocols}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </FormSection>

              {/* === 5. 進階防審查 === */}
              <FormSection
                title="進階防審查"
                description="uTLS 指紋切換、連線輪替、DNS TXT bootstrap；多數節點留預設即可，僅在 GFW 偵測時才需調整。"
              >
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="AB-D6 動態 JA4 換指紋"
                  description="留空 = 用客戶端內建預設（chrome / firefox / safari 三色分散）；GFW 學到某個 JA4 hash 時，這裡改下拉就能 30 秒內透過 heartbeat 推給所有用戶，不用發 client release。值來自 sing-box utls_client.go 支援清單。"
                />
                <Row gutter={ROW_GUTTER}>
                  {FINGERPRINT_PROTOCOL_KEYS.map((protoKey) => (
                    <Col {...COL_HALF} key={protoKey}>
                      <Form.Item
                        name={['fingerprintMap', protoKey]}
                        label={FINGERPRINT_PROTOCOL_LABELS[protoKey]}
                      >
                        <Select
                          allowClear
                          placeholder="使用客戶端預設"
                          options={FINGERPRINT_VALUE_OPTIONS.filter(
                            (opt) => opt.value !== ''
                          ).map((opt) => ({
                            value: opt.value,
                            label: opt.label,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  ))}

                  <Col xs={24}>
                    <Typography.Text strong style={{ display: 'block', marginTop: 8, marginBottom: 8 }}>
                      Connection Rotation
                    </Typography.Text>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name="connectionRotationEnabled"
                      label="啟用連線輪替"
                      valuePropName="checked"
                      tooltip="啟用後 client 會在設定窗口內重取 connect ticket 並重建 profile；會短暫重連。"
                    >
                      <Switch checkedChildren="啟用" unCheckedChildren="停用" />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item
                      name="connectionRotationMinMinutes"
                      label="最短分鐘"
                      rules={[{ type: 'number', min: 15, max: 1440, message: '範圍為 15-1440 分鐘' }]}
                    >
                      <InputNumber min={15} max={1440} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item
                      name="connectionRotationMaxMinutes"
                      label="最長分鐘"
                      rules={[{ type: 'number', min: 15, max: 1440, message: '範圍為 15-1440 分鐘' }]}
                    >
                      <InputNumber min={15} max={1440} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Typography.Text strong style={{ display: 'block', marginTop: 8, marginBottom: 8 }}>
                      DNS TXT Discovery Bootstrap
                    </Typography.Text>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name="dnsTxtDiscoveryEnabled"
                      label="啟用 DNS TXT Bootstrap"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="啟用" unCheckedChildren="停用" />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item
                      name="dnsTxtMaxAgeSec"
                      label="TXT Max Age 秒"
                      rules={[{ type: 'number', min: 60, max: 86400, message: '範圍為 60-86400 秒' }]}
                    >
                      <InputNumber min={60} max={86400} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item
                      name="dnsTxtExpectedKid"
                      label="Expected Key ID"
                      dependencies={['dnsTxtDiscoveryEnabled']}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator: (_, value) =>
                            getFieldValue('dnsTxtDiscoveryEnabled') && !value?.trim()
                              ? Promise.reject(
                                  new Error('啟用 DNS TXT Bootstrap 必須設定 Expected Key ID')
                                )
                              : Promise.resolve(),
                        }),
                      ]}
                    >
                      <Input placeholder="例如 kid-2026-04" />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item
                      name="dnsTxtNamesText"
                      label="TXT Names（每行一個）"
                      dependencies={['dnsTxtDiscoveryEnabled']}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator: (_, value) =>
                            getFieldValue('dnsTxtDiscoveryEnabled')
                              ? validateRequiredTextList(
                                  value,
                                  '啟用 DNS TXT Bootstrap 必須設定 TXT Names'
                                )
                              : Promise.resolve(),
                        }),
                      ]}
                    >
                      <TextArea rows={3} placeholder="_kshield.edge.example.com" />
                    </Form.Item>
                  </Col>
                  <Col {...COL_HALF}>
                    <Form.Item
                      name="dnsTxtDohEndpointsText"
                      label="DoH Endpoints（每行一個）"
                      dependencies={['dnsTxtDiscoveryEnabled']}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator: (_, value) =>
                            validateHttpsUrlList(
                              value,
                              getFieldValue('dnsTxtDiscoveryEnabled')
                            ),
                        }),
                      ]}
                    >
                      <TextArea rows={3} placeholder="https://cloudflare-dns.com/dns-query" />
                    </Form.Item>
                  </Col>
                </Row>
              </FormSection>

              {/* === 6. DAITA (Sprint 19 Wave 6 / W6-A) === */}
              <DaitaFormSection form={form} />
            </>
          )
        }
      </Form.Item>
    </Form>
  );
};

// =====================================================================
// Sprint 19 Wave 6 (W6-A) — DAITA authoring section
// =====================================================================

/**
 * Tri-state visual + collapsible DAITA section. Per W3-H research:
 *   - GREEN CHECK: enabled + machine spec set → ready (Wave 6 activation gate not flipped)
 *   - GREY:        disabled → authoring draft / default
 *   - AMBER WARN:  iOS-only fleet warning since libbox WITH_DAITA env still false
 *
 * Wave 6 keeps this AUTHORING-only — toggling `enabled=true` here does NOT
 * activate DAITA in production until the libbox WITH_DAITA Jenkins env flips
 * and the W6-B ACL union ships. The amber banner is the visible reminder.
 */
interface DaitaFormSectionProps {
  form: ReturnType<typeof Form.useForm>[0];
}

const DaitaFormSection: React.FC<DaitaFormSectionProps> = ({ form }) => {
  // Subscribe to relevant DAITA fields so the tri-state badge / banners
  // rerender when the operator toggles `daitaEnabled` or pastes specs.
  const daitaEnabled = Form.useWatch('daitaEnabled', form) === true;
  const machineSpec: string =
    (Form.useWatch('daitaMachineSpec', form) as string | undefined) ?? '';
  const replyMachineSpec: string =
    (Form.useWatch('daitaReplyMachineSpec', form) as string | undefined) ?? '';

  const hasSpec = machineSpec.trim().length > 0 || replyMachineSpec.trim().length > 0;

  let stateBadge: React.ReactNode;
  if (daitaEnabled && hasSpec) {
    stateBadge = (
      <Tag
        icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
        color="success"
        data-testid="daita-state-tag"
      >
        DAITA 啟用（authoring；Wave 6 啟動前 inert）
      </Tag>
    );
  } else if (daitaEnabled && !hasSpec) {
    stateBadge = (
      <Tag
        icon={<WarningTwoTone twoToneColor="#faad14" />}
        color="warning"
        data-testid="daita-state-tag"
      >
        DAITA enabled 但缺 machineSpec / replyMachineSpec
      </Tag>
    );
  } else {
    stateBadge = (
      <Tag
        icon={<MinusCircleTwoTone twoToneColor="#bfbfbf" />}
        color="default"
        data-testid="daita-state-tag"
      >
        DAITA 停用（authoring draft）
      </Tag>
    );
  }

  return (
    <FormSection
      title={
        <Space>
          <span>DAITA — Defense Against AI Traffic Analysis</span>
          {stateBadge}
        </Space>
      }
      description="Per-node Maybenot v2 流量整形（cover packets + blocking gates）；對抗 ML 流量分析。Mullvad 2024-05 上線、iOS 2024-07，K盾 vendored maybenot v2.2.2。"
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="平台支援狀態：Android(Sprint 19 已 ship 但 libbox INERT) / iOS(W4-A 已 plumbing 但 libbox INERT) / macOS+Windows(Wave 5 待做)"
        description={
          <>
            這個面板只是 authoring，**設定不會立即生效**。Wave 6 activation gate 需要:
            <ol style={{ margin: '4px 0 0 16px', padding: 0 }}>
              <li>Jenkins `RPVPN_WITH_DAITA=true` 重 build libbox（Wave 6 prereq）</li>
              <li>W6-B `vpn_tier.can_access_daita` ACL UNION 上線</li>
              <li>libbox + maybenot-ffi.a 對應 client release（2.0.16+）</li>
            </ol>
            目前所有 production client libbox 都是 stub build，`daita.enabled=true` 在 device 上會被 sing-box config-load fail-closed（stub 回 `maybenot: not included in this build`）。
            電量成本參考：Mullvad iOS 實測 +2-4% / day（iPhone SE / 13 mini 級）。
          </>
        }
      />
      <Row gutter={ROW_GUTTER}>
        <Col {...COL_HALF}>
          <Form.Item
            name="daitaEnabled"
            label="啟用 DAITA"
            valuePropName="checked"
            tooltip="開啟後此節點 nodeConfigJson.daita.enabled=true，client 收到 `da` envelope 後（Android Np.kt applyDaita / iOS box.swift applyDaitaIos）stamp maybenot_* 到 AnyTLS+ShadowTLS outbound。Reality 不套用（per W3-H §2.4 Mullvad reference）。"
          >
            <Switch
              checkedChildren="啟用"
              unCheckedChildren="停用"
              data-testid="daita-enabled-switch"
            />
          </Form.Item>
        </Col>
        <Col {...COL_HALF}>
          <Form.Item
            name="daitaMaxPaddingFrac"
            label="MaxPaddingFrac（cover-traffic budget 0.0-1.0）"
            tooltip="Maybenot 允許的 cover packet 比例上限。預設 0.5（與 box.swift applyDaitaIos clamp default 一致）。範圍 0-1，server 端 normalize clamp。"
            rules={[
              { type: 'number', min: 0, max: 1, message: '範圍為 0.0-1.0' },
            ]}
          >
            <Slider
              min={0}
              max={1}
              step={0.01}
              marks={{ 0: '0', 0.25: '0.25', 0.5: '0.5', 0.75: '0.75', 1: '1' }}
              data-testid="daita-mpf-slider"
            />
          </Form.Item>
        </Col>
        <Col {...COL_HALF}>
          <Form.Item
            name="daitaMaxBlockingFrac"
            label="MaxBlockingFrac（real-traffic gating budget 0.0-1.0）"
            tooltip="Maybenot 允許阻擋實際 packet 的比例上限。預設 0.1（與 box.swift applyDaitaIos clamp default 一致）。值越大 anti-fingerprint 越強但延遲越高。"
            rules={[
              { type: 'number', min: 0, max: 1, message: '範圍為 0.0-1.0' },
            ]}
          >
            <Slider
              min={0}
              max={1}
              step={0.01}
              marks={{ 0: '0', 0.1: '0.1', 0.25: '0.25', 0.5: '0.5', 1: '1' }}
              data-testid="daita-mbf-slider"
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="daitaMachineSpec"
            label="Machine Spec（client-side maybenot v2 機器規格）"
            tooltip="Maybenot v2 client-side machine spec — opaque base64 / vendor format（vendor/maybenot/crates/maybenot-ffi）。BO 不解析內容，只 length-cap ≤ 16384。stamper（Np.kt / box.swift）原樣傳給 sing-box 對應 outbound `maybenot_machine_spec` 欄位。"
            rules={[
              {
                max: 16384,
                message: 'machineSpec 長度不可超過 16384 字',
              },
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  const enabled = getFieldValue('daitaEnabled') === true;
                  const reply = (getFieldValue('daitaReplyMachineSpec') as string | undefined)?.trim() ?? '';
                  const me = typeof value === 'string' ? value.trim() : '';
                  if (enabled && !me && !reply) {
                    return Promise.reject(
                      new Error('啟用 DAITA 時必須至少設定 machineSpec 或 replyMachineSpec')
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <TextArea
              rows={4}
              placeholder="opaque base64 machine spec — operator 從 maybenot 工具產生"
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 12 }}
              data-testid="daita-machine-spec-input"
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="daitaReplyMachineSpec"
            label="Reply Machine Spec（server→client 方向 maybenot v2 規格）"
            tooltip="Maybenot v2 reply (server→client direction) machine spec — opaque base64 / vendor format。stamper 傳給 sing-box `maybenot_reply_machine_spec`。可只填 client 端或只填 reply 端，但 enabled=true 時至少要有一個。"
            rules={[
              {
                max: 16384,
                message: 'replyMachineSpec 長度不可超過 16384 字',
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="opaque base64 reply machine spec"
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 12 }}
              data-testid="daita-reply-machine-spec-input"
            />
          </Form.Item>
        </Col>
      </Row>
    </FormSection>
  );
};
